import { BaseFormModeAdapter } from "./adapters";
import {
  FormConfiguration,
  SubmissionContext,
  FormInitParams,
} from "./definitions";
import {
  transformScanResultToFormData,
  buildDraftFormResetData,
} from "../util";

/**
 * Adapter for Draft Wizard mode (with assessment)
 * Handles form behavior for wizard-based draft processing with assessment steps
 * This is used for draft workflows that include wizard steps and assessment creation
 */
export class WizardAdapter extends BaseFormModeAdapter {
  /**
   * Initialize form data for wizard mode
   */
  initializeFormData(params: FormInitParams): void {
    const {
      reset,
      scanResultData,
      scanJobData,
      customAttributes,
      uiContent,
      currentUser,
      localFormData,
      shortId,
    } = params;

    // WizardAdapter always tries to merge local storage data if available
    if (localFormData && shortId) {
      // If we have local storage data, merge it with scan data (if available)
      if (scanResultData && customAttributes) {
        const ownerValue =
          scanJobData?.scanJob?.createdBy || currentUser?.alias;
        const scanFormData = transformScanResultToFormData(
          scanResultData.scanResult,
          customAttributes,
          uiContent,
          ownerValue,
        );
        const mergedData = {
          ...buildDraftFormResetData(scanFormData),
          ...localFormData,
        };
        reset(mergedData);
      } else {
        // Only local storage data available
        reset(localFormData);
      }
    } else if (scanResultData && customAttributes) {
      // Only scan data available, no local storage
      const ownerValue = scanJobData?.scanJob?.createdBy || currentUser?.alias;
      const scanFormData = transformScanResultToFormData(
        scanResultData.scanResult,
        customAttributes,
        uiContent,
        ownerValue,
      );
      reset(buildDraftFormResetData(scanFormData));
    }
  }

  /**
   * Get form configuration for wizard mode
   */
  getFormConfig(): FormConfiguration {
    return {
      ...super.getFormConfig(),
      // Wizard mode specific overrides
      showTemplates: false,
      showFileUpload: false,
      enableNavigation: false,
    };
  }

  shouldLoadScanResult(): boolean {
    return true;
  }

  shouldLoadScanJob(): boolean {
    return true;
  }

  shouldPersistFormData(shortId?: string): boolean {
    return !!shortId;
  }

  /**
   * Handle form submission for wizard mode
   * Wizard mode does NOT submit - it only validates and passes data to parent wizard
   */
  async handleSubmit(data: any, context: SubmissionContext): Promise<void> {
    // Wizard mode: only validate and pass to parent
    if (context.ui.onWizardValidationSuccess) {
      context.ui.onWizardValidationSuccess(data);
    }
  }
}
